// REV-586 — True double-entry journal entry modal
// Replaces CreateTransactionAdjustmentModal for the ViewRegister flow.
// The opening-balance (callingType='openning_cash') flow still uses the old modal.

import React, { useRef, useState, useEffect, useMemo } from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import AsyncSelect from 'react-select/async';

import SAlert from '../../../components/SAlert';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { currencyFormat } from '../../../resources/helper';
import CurrencyInput from 'react-currency-input-field';
import DatePicker from 'react-datepicker';
import '../../../assets/css/datepicker.css';
import { JournalEntryFormI, JournalEntryRowI } from '../../../resources/form-props';
import { SELECT_CUSTOM_STYLE } from '../../../resources/constants';
import SelectCustomOption from '../../../components/SelectCustomOption';
import { yupResolver } from '@hookform/resolvers/yup';
import { JournalEntryValidate } from '../../../resources/form-validator';

// ─── Props ───────────────────────────────────────────────────────────────────

interface JournalEntryModalProps {
    show: boolean;
    onHide: () => void;
    /** Receives the validated rows + header fields + optional vendorId; called only after SAlert confirm */
    onSave: (rows: JournalEntryRowI[], headerDescription: string, transactionAt: Date, vendorId?: number | null) => void;
    glAccounts: any[];
    filterAllAccountList: (inputValue: string) => Promise<any[]>;
    selectedCalendar: { year: number; month: number; day?: number; [key: string]: any };
    isSaving?: boolean;
    /**
     * Set by the parent after a successful API call.
     * When non-null the modal switches to a "saved" confirmation panel
     * showing the transaction ID before the user dismisses it.
     */
    savedTransactionId?: number | null;
    /** Overrides the default modal title (e.g. "Beginning Cash Adjustment") */
    modalTitle?: string;
    vendors?: Array<{ id: number; vendor_code: string; display_name: string; [key: string]: any }>;
    onOpenQuickAddVendor?: (searchQuery: string) => void;
    initialVendorId?: number | null;
}

// ─── Blank row factory ────────────────────────────────────────────────────────

const blankRow = (): JournalEntryRowI => ({
    master_chart_of_account_id: null,
    debit_amount: 0,
    credit_amount: 0,
    notes: '',
});

// ─── Component ───────────────────────────────────────────────────────────────

const JournalEntryModal: React.FC<JournalEntryModalProps> = ({
    show,
    onHide,
    onSave,
    glAccounts,
    filterAllAccountList,
    selectedCalendar,
    isSaving = false,
    savedTransactionId = null,
    modalTitle,
    vendors = [],
    onOpenQuickAddVendor,
    initialVendorId = null,
}) => {
    const { t } = useTranslation('components.Modal.ViewRegister.JournalEntryModal');

    const selectCustomStyle = SELECT_CUSTOM_STYLE('#d1ffbd');

    // ── Refs ──────────────────────────────────────────────────────────────────
    /** Focused when Tab is pressed on the last input of the last row and the entry is balanced */
    const reviewButtonRef = useRef<HTMLButtonElement>(null);

    // ── Modal-level state ──────────────────────────────────────────────────────
    const [showAlert, setShowAlert] = useState(false);
    const [isReviewMode, setIsReviewMode] = useState(false);
    /** Snapshot captured on "Review Entries" — used for display and final submit */
    const [pendingData, setPendingData] = useState<JournalEntryFormI | null>(null);
    /**
     * Raw string values for each CurrencyInput, keyed by field.id (stable across
     * re-renders).  Storing the raw string (e.g. "1.", "1.2", "1.25") lets the
     * user type cents without the decimal point being eaten by toFixed(2).
     */
    const [rawInputs, setRawInputs] = useState<Record<string, { debit: string; credit: string }>>({});

    // ── Vendor state ──────────────────────────────────────────────────────────
    const [selectedVendorId, setSelectedVendorId] = useState<number | null>(initialVendorId || null);
    const [vendorSearchQuery, setVendorSearchQuery] = useState('');
    const [showVendorDropdown, setShowVendorDropdown] = useState(false);
    const vendorDropdownRef = useRef<HTMLDivElement>(null);

    const selectedVendor = useMemo(
        () => vendors.find((v) => v.id === selectedVendorId),
        [vendors, selectedVendorId]
    );

    useEffect(() => {
        if (initialVendorId !== undefined) {
            setSelectedVendorId(initialVendorId);
        }
    }, [initialVendorId]);

    useEffect(() => {
        if (selectedVendor) {
            setVendorSearchQuery(`${selectedVendor.vendor_code} — ${selectedVendor.display_name}`);
        } else if (!showVendorDropdown) {
            setVendorSearchQuery('');
        }
    }, [selectedVendor, showVendorDropdown]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                vendorDropdownRef.current &&
                !vendorDropdownRef.current.contains(event.target as Node)
            ) {
                setShowVendorDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredVendors = useMemo(() => {
        if (!vendorSearchQuery.trim()) {
            return vendors;
        }
        const q = vendorSearchQuery.toLowerCase().trim();
        return vendors.filter(
            (v) =>
                (v.display_name && v.display_name.toLowerCase().includes(q)) ||
                (v.vendor_code && v.vendor_code.toLowerCase().includes(q))
        );
    }, [vendors, vendorSearchQuery]);

    // ── Form ──────────────────────────────────────────────────────────────────
    const defaultDate = new Date(
        selectedCalendar.year,
        selectedCalendar.month - 1,
        selectedCalendar.day ?? 1
    );

    const {
        handleSubmit,
        control,
        formState: { errors },
        reset,
        setValue,
        watch,
        trigger,
    } = useForm<JournalEntryFormI>({
        defaultValues: {
            transaction_at: defaultDate,
            header_description: '',
            rows: [blankRow(), blankRow()],
        },
        resolver: yupResolver(JournalEntryValidate) as any,
        mode: 'onChange',
    });

    const { fields, append, remove } = useFieldArray({ control, name: 'rows' });
    const watchedRows = watch('rows');
    const watchedDate = watch('transaction_at');

    // ── Computed totals (derived — no extra state) ─────────────────────────────
    const totalDebit  = watchedRows?.reduce((s, r) => s + (r.debit_amount  || 0), 0) ?? 0;
    const totalCredit = watchedRows?.reduce((s, r) => s + (r.credit_amount || 0), 0) ?? 0;
    const imbalance   = +(totalDebit - totalCredit).toFixed(2);
    const isBalanced  = totalDebit > 0 && Math.abs(imbalance) < 0.01;

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleClose = () => {
        reset({
            transaction_at: defaultDate,
            header_description: '',
            rows: [blankRow(), blankRow()],
        });
        setIsReviewMode(false);
        setPendingData(null);
        setRawInputs({});
        setSelectedVendorId(null);
        setVendorSearchQuery('');
        setShowVendorDropdown(false);
        onHide();
    };

    /** "Review Entries" — validate first, then snapshot and enter review mode */
    const goToReview = (data: JournalEntryFormI) => {
        setPendingData(data);
        setIsReviewMode(true);
    };

    /** "Back to Edit" */
    const backToEdit = () => {
        setIsReviewMode(false);
    };

    /** "Submit" in review mode — show SAlert confirmation */
    const handleSubmitFromReview = () => {
        if (!pendingData || isSaving) return;
        setShowAlert(true);
    };

    /** SAlert confirmed — call parent onSave */
    const processData = () => {
        if (!pendingData || isSaving) return;
        setShowAlert(false);
        onSave(pendingData.rows, pendingData.header_description, pendingData.transaction_at, selectedVendorId);
    };

    /** Add a blank row */
    const handleAddRow = () => append(blankRow());

    /** Remove a row — guard: keep minimum 2 rows */
    const handleRemoveRow = (index: number) => {
        if (fields.length <= 2) return;
        remove(index);
    };

    /**
     * Debit/Credit mutual exclusion per row.
     * Entering a value in Debit clears Credit for that row, and vice versa.
     *
     * fieldId comes from useFieldArray field.id — stable identity, used to key
     * rawInputs so the raw string (including mid-entry "1.") survives re-renders.
     */
    const handleDebitChange = (index: number, fieldId: string, value: string | undefined) => {
        const num = (value && +value) || 0;
        setValue(`rows.${index}.debit_amount`, num);
        if (num > 0) {
            setValue(`rows.${index}.credit_amount`, 0);
            // Also clear the displayed credit string for this row
            setRawInputs(prev => ({ ...prev, [fieldId]: { debit: value ?? '', credit: '' } }));
        } else {
            setRawInputs(prev => ({ ...prev, [fieldId]: { ...prev[fieldId], debit: value ?? '' } }));
        }
        trigger('rows');
    };

    const handleCreditChange = (index: number, fieldId: string, value: string | undefined) => {
        const num = (value && +value) || 0;
        setValue(`rows.${index}.credit_amount`, num);
        if (num > 0) {
            setValue(`rows.${index}.debit_amount`, 0);
            // Also clear the displayed debit string for this row
            setRawInputs(prev => ({ ...prev, [fieldId]: { credit: value ?? '', debit: '' } }));
        } else {
            setRawInputs(prev => ({ ...prev, [fieldId]: { ...prev[fieldId], credit: value ?? '' } }));
        }
        trigger('rows');
    };

    /**
     * On blur: format the raw string to two decimal places so "100" → "100.00".
     * Empty / zero values are cleared to '' so the placeholder "0.00" shows instead.
     */
    const handleDebitBlur = (fieldId: string) => {
        setRawInputs(prev => {
            const raw = (prev[fieldId]?.debit ?? '').trim();
            if (!raw || raw === '0' || raw === '0.00') {
                return { ...prev, [fieldId]: { ...prev[fieldId], debit: '' } };
            }
            const num = parseFloat(raw);
            if (isNaN(num)) return prev;
            return { ...prev, [fieldId]: { ...prev[fieldId], debit: num.toFixed(2) } };
        });
    };

    const handleCreditBlur = (fieldId: string) => {
        setRawInputs(prev => {
            const raw = (prev[fieldId]?.credit ?? '').trim();
            if (!raw || raw === '0' || raw === '0.00') {
                return { ...prev, [fieldId]: { ...prev[fieldId], credit: '' } };
            }
            const num = parseFloat(raw);
            if (isNaN(num)) return prev;
            return { ...prev, [fieldId]: { ...prev[fieldId], credit: num.toFixed(2) } };
        });
    };

    /**
     * Smart Tab / Enter keyboard navigation:
     *
     * Tab (forward) on the Notes field (data-last-input="true") of the last row:
     *   • Entry NOT balanced → append a new blank row and focus its first input
     *   • Entry IS balanced  → jump focus straight to the "Review Entries" button
     *
     * Shift+Tab and Tab on any other field: browser default (no interference).
     *
     * Enter anywhere in the form: move focus to the next focusable element
     * (prevents accidental form submission and mimics Tab behaviour for
     * users who prefer Enter to advance through fields).
     */
    const handleKeyNavigation = (event: React.KeyboardEvent<HTMLFormElement>) => {
        if (event.key !== 'Enter' && event.key !== 'Tab') return;

        const activeEl = document.activeElement as HTMLElement;

        if (event.key === 'Tab' && !event.shiftKey) {
            // Only act when focus is on the designated last-input of a row
            if (activeEl?.getAttribute('data-last-input') !== 'true') return;

            // Only act on the last row
            const rowIndex = activeEl.closest('tr')?.getAttribute('data-row-index');
            if (rowIndex !== String(fields.length - 1)) return;

            event.preventDefault();

            if (!isBalanced) {
                // Append new row and focus its first focusable input
                append(blankRow());
                setTimeout(() => {
                    const tbody = event.currentTarget.querySelector('tbody');
                    const rows = tbody?.querySelectorAll('tr');
                    const newRow = rows?.[rows.length - 1];
                    (newRow?.querySelector('input') as HTMLElement | null)?.focus();
                }, 50);
            } else {
                // Entry is balanced — skip straight to the Review Entries button
                reviewButtonRef.current?.focus();
            }
            return;
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            const focusable = Array.from(
                event.currentTarget.querySelectorAll<HTMLElement>(
                    'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'
                )
            );
            const idx = focusable.indexOf(activeEl);
            if (idx > -1 && idx < focusable.length - 1) {
                focusable[idx + 1].focus();
            }
        }
    };

    // ── Row — edit mode ────────────────────────────────────────────────────────

    const renderEditRow = (field: any, index: number) => {
        const rowErrors = (errors?.rows?.[index] as any);
        const currentDebit  = watchedRows?.[index]?.debit_amount  || 0;
        const currentCredit = watchedRows?.[index]?.credit_amount || 0;

        return (
            <tr key={field.id} data-row-index={index}>
                {/* GL Account */}
                <td style={{ minWidth: 220 }}>
                    <Controller
                        control={control}
                        name={`rows.${index}.master_chart_of_account_id`}
                        render={({ field: { value, onChange } }) => (
                            <>
                                <AsyncSelect
                                    isClearable
                                    placeholder={t('modal.input.account.placeholder')}
                                    defaultOptions={glAccounts}
                                    loadOptions={filterAllAccountList}
                                    onChange={onChange}
                                    value={value}
                                    styles={selectCustomStyle}
                                    components={{ Option: SelectCustomOption }}
                                />
                                {/* Row-level errors (account-required, exactly-one-side) land on rowErrors.message;
                                    field-level errors (if any) land on rowErrors.master_chart_of_account_id.message */}
                                {(rowErrors?.message || rowErrors?.master_chart_of_account_id?.message) && (
                                    <div className="text-danger small mt-1">
                                        {rowErrors?.message || rowErrors?.master_chart_of_account_id?.message}
                                    </div>
                                )}
                            </>
                        )}
                    />
                </td>

                {/* Debit */}
                <td style={{ width: 130 }}>
                    <Controller
                        name={`rows.${index}.debit_amount`}
                        control={control}
                        render={({ field: controllerField }) => (
                            <CurrencyInput
                                className={`form-control text-end ${currentDebit > 0 ? 'border-warning' : ''}`}
                                placeholder="0.00"
                                // Use the raw string the user typed (preserves "1." mid-entry).
                                // Falls back to '' so the placeholder shows on blank rows.
                                value={rawInputs[field.id]?.debit ?? ''}
                                onValueChange={(val) => handleDebitChange(index, field.id, val)}
                                onBlur={() => handleDebitBlur(field.id)}
                                allowDecimals
                                decimalsLimit={2}
                                disabled={currentCredit > 0}
                            />
                        )}
                    />
                </td>

                {/* Credit */}
                <td style={{ width: 130 }}>
                    <Controller
                        name={`rows.${index}.credit_amount`}
                        control={control}
                        render={({ field: controllerField }) => (
                            <CurrencyInput
                                className={`form-control text-end ${currentCredit > 0 ? 'border-success' : ''}`}
                                placeholder="0.00"
                                // Same raw-string approach for cents entry.
                                value={rawInputs[field.id]?.credit ?? ''}
                                onValueChange={(val) => handleCreditChange(index, field.id, val)}
                                onBlur={() => handleCreditBlur(field.id)}
                                allowDecimals
                                decimalsLimit={2}
                                disabled={currentDebit > 0}
                            />
                        )}
                    />
                </td>

                {/* Notes */}
                <td>
                    <Controller
                        name={`rows.${index}.notes`}
                        control={control}
                        render={({ field }) => (
                            <Form.Control
                                {...field}
                                type="text"
                                placeholder={t('modal.input.notes.placeholder')}
                                data-last-input="true"
                            />
                        )}
                    />
                </td>

                {/* Delete */}
                <td className="text-center align-middle" style={{ width: 40 }}>
                    {fields.length > 2 && (
                        <i
                            className="fa fa-trash fa-lg text-danger"
                            style={{ cursor: 'pointer' }}
                            tabIndex={0}
                            role="button"
                            onClick={() => handleRemoveRow(index)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleRemoveRow(index);
                                }
                            }}
                        />
                    )}
                </td>
            </tr>
        );
    };

    // ── Row — review mode ──────────────────────────────────────────────────────

    const renderReviewRow = (row: JournalEntryRowI, index: number) => (
        <tr key={index} style={{ background: '#fffdf0' }}>
            <td style={{ fontWeight: 600 }}>
                {row.master_chart_of_account_id?.label ?? '—'}
            </td>
            <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {row.debit_amount > 0 ? currencyFormat(row.debit_amount.toFixed(2)) : '—'}
            </td>
            <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {row.credit_amount > 0 ? currencyFormat(row.credit_amount.toFixed(2)) : '—'}
            </td>
            <td style={{ color: '#555' }}>{row.notes || ''}</td>
        </tr>
    );

    // ── JSX ───────────────────────────────────────────────────────────────────

    return (
        <Modal
            show={show}
            onHide={handleClose}
            size="xl"
            className="modal-custom-xxl"
            animation
            backdrop="static"
        >
            <Modal.Header closeButton>
                <Modal.Title>{modalTitle ?? t('modal.title')}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {/* ── Success panel (shown after API save returns the transaction ID) ── */}
                {savedTransactionId != null && (
                    <div className="text-center py-4">
                        <div style={{ fontSize: 56 }}>✅</div>
                        <h4 className="mt-3 mb-1" style={{ color: '#15803d' }}>
                            {t('modal.success.title')}
                        </h4>
                        <p className="text-muted mb-3">{t('modal.success.subtitle')}</p>
                        <div
                            className="d-inline-flex align-items-center gap-2 px-4 py-2 rounded"
                            style={{ background: '#f0fdf4', border: '1.5px solid #86efac', fontSize: 18 }}
                        >
                            <span className="text-muted" style={{ fontSize: 14 }}>
                                {t('modal.success.transaction_id_label')}
                            </span>
                            <strong style={{ color: '#166534', fontVariantNumeric: 'tabular-nums', letterSpacing: 1 }}>
                                #{savedTransactionId}
                            </strong>
                        </div>
                        <div className="mt-4">
                            <Button variant="success" onClick={handleClose}>
                                {t('modal.button.close')}
                            </Button>
                        </div>
                    </div>
                )}

                {/* ── Review banner ─────────────────────────────────────────── */}
                {!savedTransactionId && isReviewMode && (
                    <div
                        className="d-flex align-items-center gap-2 mb-3 p-2 rounded"
                        style={{ background: '#fffbeb', border: '1.5px solid #f59e0b', color: '#92400e', fontSize: 13 }}
                    >
                        <i className="fa fa-eye me-1" />
                        <strong>Review Mode</strong> — Verify entries before submitting.
                    </div>
                )}

                {/* ── Edit mode form ────────────────────────────────────────── */}
                {!savedTransactionId && !isReviewMode && (
                    <Form
                        className="journal-entry-form"
                        onSubmit={handleSubmit(goToReview)}
                        onKeyDown={handleKeyNavigation}
                    >
                        {/* Header row: TRX Date + Vendor + Journal Entry Description + Balanced badge */}
                        <div className="row align-items-end mb-3">
                            <div className="col-md-2">
                                <Form.Label><strong>{t('modal.label.date')}</strong></Form.Label>
                                <Controller
                                    name="transaction_at"
                                    control={control}
                                    render={({ field }) => (
                                        <DatePicker
                                            selected={field.value}
                                            onChange={field.onChange}
                                            className="form-control"
                                            dateFormat="MM/dd/yyyy"
                                            minDate={moment(`${selectedCalendar.year}/${selectedCalendar.month}/01`, 'YYYY/MM/DD').toDate()}
                                            maxDate={moment(`${selectedCalendar.year}/${selectedCalendar.month}/01`, 'YYYY/MM/DD').endOf('month').toDate()}
                                        />
                                    )}
                                />
                                {errors.transaction_at && (
                                    <div className="text-danger small mt-1">{errors.transaction_at.message as string}</div>
                                )}
                            </div>

                            <div className="col-md-3" ref={vendorDropdownRef}>
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                    <Form.Label className="mb-0">
                                        <strong>{t('modal.label.vendor', 'Vendor')}</strong>
                                    </Form.Label>
                                    {onOpenQuickAddVendor && (
                                        <button
                                            type="button"
                                            className="btn btn-link p-0 text-primary small text-decoration-none fw-semibold"
                                            style={{ fontSize: 12 }}
                                            onClick={() => {
                                                setShowVendorDropdown(false);
                                                onOpenQuickAddVendor(
                                                    selectedVendor ? '' : vendorSearchQuery.trim()
                                                );
                                            }}
                                        >
                                            + New Vendor
                                        </button>
                                    )}
                                </div>
                                <div className="position-relative">
                                    <Form.Control
                                        type="search"
                                        placeholder={t('modal.input.vendor.placeholder', 'Type to search vendor...')}
                                        value={vendorSearchQuery}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setVendorSearchQuery(val);
                                            setShowVendorDropdown(true);
                                            if (!val) {
                                                setSelectedVendorId(null);
                                            }
                                        }}
                                        onFocus={() => setShowVendorDropdown(true)}
                                        autoComplete="off"
                                    />
                                    {selectedVendorId && (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-link text-muted position-absolute end-0 top-50 translate-middle-y text-decoration-none"
                                            style={{ padding: '0 10px', fontSize: '14px', zIndex: 5 }}
                                            onClick={() => {
                                                setSelectedVendorId(null);
                                                setVendorSearchQuery('');
                                                setShowVendorDropdown(false);
                                            }}
                                            title="Clear vendor"
                                        >
                                            ✕
                                        </button>
                                    )}
                                    {showVendorDropdown && (
                                        <div
                                            className="dropdown-menu show w-100 shadow border"
                                            style={{
                                                maxHeight: '220px',
                                                overflowY: 'auto',
                                                zIndex: 1060,
                                                position: 'absolute',
                                                top: '100%',
                                                left: 0,
                                            }}
                                        >
                                            {filteredVendors.length === 0 ? (
                                                <div className="p-2">
                                                    <div className="small text-muted mb-1 px-2">
                                                        {vendorSearchQuery.trim()
                                                            ? `No vendors matching "${vendorSearchQuery.trim()}"`
                                                            : 'No vendors available'}
                                                    </div>
                                                    {onOpenQuickAddVendor && vendorSearchQuery.trim() && (
                                                        <button
                                                            type="button"
                                                            className="dropdown-item small text-primary fw-bold rounded"
                                                            onClick={() => {
                                                                setShowVendorDropdown(false);
                                                                onOpenQuickAddVendor(vendorSearchQuery.trim());
                                                            }}
                                                        >
                                                            + Create "{vendorSearchQuery.trim()}" on the fly
                                                        </button>
                                                    )}
                                                </div>
                                            ) : (
                                                <>
                                                    {onOpenQuickAddVendor && vendorSearchQuery.trim() && !filteredVendors.some(v => v.display_name.toLowerCase() === vendorSearchQuery.trim().toLowerCase()) && (
                                                        <button
                                                            type="button"
                                                            className="dropdown-item small text-primary fw-bold border-bottom"
                                                            onClick={() => {
                                                                setShowVendorDropdown(false);
                                                                onOpenQuickAddVendor(vendorSearchQuery.trim());
                                                            }}
                                                        >
                                                            + Create "{vendorSearchQuery.trim()}" on the fly
                                                        </button>
                                                    )}
                                                    {filteredVendors.map((v) => (
                                                        <button
                                                            key={v.id}
                                                            type="button"
                                                            className={`dropdown-item small ${v.id === selectedVendorId ? 'active fw-bold' : ''}`}
                                                            onClick={() => {
                                                                setSelectedVendorId(v.id);
                                                                setVendorSearchQuery(`${v.vendor_code} — ${v.display_name}`);
                                                                setShowVendorDropdown(false);
                                                            }}
                                                        >
                                                            <span className="fw-bold">{v.vendor_code}</span> — {v.display_name}
                                                        </button>
                                                    ))}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="col-md-4">
                                <Form.Label>
                                    <strong>{t('modal.label.journal_entry_description')}</strong>
                                </Form.Label>
                                <Controller
                                    name="header_description"
                                    control={control}
                                    render={({ field }) => (
                                        <Form.Control
                                            {...field}
                                            type="text"
                                            placeholder={t('modal.input.description.placeholder')}
                                            isInvalid={!!errors.header_description}
                                        />
                                    )}
                                />
                                {errors.header_description && (
                                    <Form.Control.Feedback type="invalid">
                                        {errors.header_description.message}
                                    </Form.Control.Feedback>
                                )}
                            </div>

                            <div className="col-md-3 d-flex justify-content-end align-items-center gap-3">
                                {isBalanced && (
                                    <span className="badge bg-success fs-6">{t('modal.label.balanced')}</span>
                                )}
                                {!isBalanced && (totalDebit > 0 || totalCredit > 0) && (
                                    <span className="badge bg-warning text-dark fs-6">
                                        {t('modal.label.imbalance', {
                                            amount: currencyFormat(Math.abs(imbalance).toFixed(2)),
                                        })}
                                    </span>
                                )}
                                <Button
                                    type="button"
                                    variant="primary"
                                    size="sm"
                                    onClick={handleAddRow}
                                >
                                    {t('modal.button.add_row')} &nbsp;<i className="fa fa-plus" />
                                </Button>
                            </div>
                        </div>

                        {/* Ledger table */}
                        <div className="card mb-3">
                            <div className="card-body p-0">
                                <table className="table table-bordered mb-0">
                                    <thead>
                                        <tr>
                                            <th>{t('modal.table.header.gl_account')}</th>
                                            <th style={{ width: 130 }} className="text-end">
                                                {t('modal.table.header.debit')}
                                            </th>
                                            <th style={{ width: 130 }} className="text-end">
                                                {t('modal.table.header.credit')}
                                            </th>
                                            <th>{t('modal.table.header.notes')}</th>
                                            <th style={{ width: 40 }} />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {fields.map((field, index) => renderEditRow(field, index))}
                                    </tbody>
                                    <tfoot>
                                        <tr style={{ background: '#f1f4f9' }}>
                                            <td className="text-end">
                                                <strong>{t('modal.label.total')}:</strong>
                                            </td>
                                            <td className="text-end">
                                                <strong style={{ fontVariantNumeric: 'tabular-nums' }}>
                                                    {currencyFormat(totalDebit.toFixed(2))}
                                                </strong>
                                            </td>
                                            <td className="text-end">
                                                <strong style={{ fontVariantNumeric: 'tabular-nums' }}>
                                                    {currencyFormat(totalCredit.toFixed(2))}
                                                </strong>
                                            </td>
                                            <td colSpan={2}>
                                                <strong style={{ color: isBalanced ? '#15803d' : '#dc2626' }}>
                                                    {t('modal.label.balance')}{' '}
                                                    {isBalanced
                                                        ? currencyFormat('0.00')
                                                        : currencyFormat(imbalance.toFixed(2))}
                                                    {isBalanced ? ' ✓' : ' ✗'}
                                                </strong>
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>

                        {/* Array-level validation errors */}
                        {errors?.rows && (errors.rows as any)?.message && (
                            <div className="alert alert-danger mt-2">
                                <strong>{(errors.rows as any).message}</strong>
                            </div>
                        )}

                        {/* Action buttons — edit mode */}
                        <div className="d-flex justify-content-end mt-3 gap-2">
                            <Button
                                ref={reviewButtonRef}
                                type="submit"
                                variant="warning"
                                disabled={isSaving}
                            >
                                {t('modal.button.review')}
                            </Button>
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={handleClose}
                                disabled={isSaving}
                            >
                                {t('modal.button.close')}
                            </Button>
                        </div>
                    </Form>
                )}

                {/* ── Review mode ───────────────────────────────────────────── */}
                {!savedTransactionId && isReviewMode && pendingData && (
                    <div className="journal-entry-review">
                        {/* Review header */}
                        <div className="row mb-3">
                            <div className="col-md-3">
                                <span className="text-muted small">{t('modal.label.date')}</span>
                                <div className="fw-bold">
                                    {moment.utc(pendingData.transaction_at).format('MM/DD/YYYY')}
                                </div>
                            </div>
                            <div className="col-md-4">
                                <span className="text-muted small">
                                    {t('modal.label.vendor', 'Vendor')}
                                </span>
                                <div className="fw-bold">
                                    {selectedVendor ? (
                                        <span className="badge bg-light text-dark border">
                                            <span className="fw-bold text-primary me-1">{selectedVendor.vendor_code}</span>
                                            {selectedVendor.display_name}
                                        </span>
                                    ) : (
                                        <span className="text-muted small">—</span>
                                    )}
                                </div>
                            </div>
                            <div className="col-md-5">
                                <span className="text-muted small">
                                    {t('modal.label.journal_entry_description')}
                                </span>
                                <div className="fw-bold">{pendingData.header_description}</div>
                            </div>
                        </div>

                        {/* Review table */}
                        <div className="card mb-3">
                            <div className="card-body p-0">
                                <table className="table table-bordered mb-0">
                                    <thead>
                                        <tr>
                                            <th>{t('modal.table.header.gl_account')}</th>
                                            <th style={{ width: 130 }} className="text-end">
                                                {t('modal.table.header.debit')}
                                            </th>
                                            <th style={{ width: 130 }} className="text-end">
                                                {t('modal.table.header.credit')}
                                            </th>
                                            <th>{t('modal.table.header.notes')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pendingData.rows.map((row, i) => renderReviewRow(row, i))}
                                    </tbody>
                                    <tfoot>
                                        <tr style={{ background: '#f1f4f9' }}>
                                            <td className="text-end">
                                                <strong>{t('modal.label.total')}:</strong>
                                            </td>
                                            <td className="text-end">
                                                <strong style={{ fontVariantNumeric: 'tabular-nums' }}>
                                                    {currencyFormat(
                                                        pendingData.rows
                                                            .reduce((s, r) => s + (r.debit_amount || 0), 0)
                                                            .toFixed(2)
                                                    )}
                                                </strong>
                                            </td>
                                            <td className="text-end">
                                                <strong style={{ fontVariantNumeric: 'tabular-nums' }}>
                                                    {currencyFormat(
                                                        pendingData.rows
                                                            .reduce((s, r) => s + (r.credit_amount || 0), 0)
                                                            .toFixed(2)
                                                    )}
                                                </strong>
                                            </td>
                                            <td>
                                                <strong style={{ color: '#15803d' }}>
                                                    {t('modal.label.balance')} {currencyFormat('0.00')} ✓
                                                </strong>
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>

                        {/* Action buttons — review mode */}
                        <div className="d-flex justify-content-end mt-3 gap-2">
                            <Button
                                type="button"
                                variant="warning"
                                onClick={backToEdit}
                                disabled={isSaving}
                            >
                                {t('modal.button.back_to_edit')}
                            </Button>
                            <Button
                                type="button"
                                variant="primary"
                                onClick={handleSubmitFromReview}
                                disabled={isSaving}
                            >
                                {isSaving ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden="true"
                                        />
                                        {t('modal.button.submit')}
                                    </>
                                ) : (
                                    t('modal.button.submit')
                                )}
                            </Button>
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={handleClose}
                                disabled={isSaving}
                            >
                                {t('modal.button.close')}
                            </Button>
                        </div>
                    </div>
                )}
            </Modal.Body>

            <SAlert
                show={showAlert}
                title={t('modal.alert.title')}
                msg={t('modal.alert.msg')}
                onConfirm={processData}
                confirmBtnText={t('modal.alert.confirm_btn')}
                cancelBtnText={t('modal.alert.cancel_btn')}
                showCancel
                onCancel={() => setShowAlert(false)}
            />
        </Modal>
    );
};

export default JournalEntryModal;
